import { Box3, PerspectiveCamera, Vector3 } from "three";
import { Engine, Entity } from "../engine/Engine";
import { CAMERA_FAR, CAMERA_NEAR } from "../engine/camera/CameraConstants";
import {
  CameraPresetId,
  getCameraPresetFromBox,
} from "../engine/camera/CameraPresets";
import { SugarModelConfigurator } from "../loader/SugarModelConfigurator";
import { SugarModelDimensions } from "../loader/SugarModelDimensions";
import { SugarModelGroup } from "../loader/SugarModelGroup";
import { logger } from "../util/Logger";
import {
  getConfiguratorData,
  CONFIGURATOR_MODEL_TYPE,
  ConfiguratorVariant,
  getVariantAttr,
  MODEL_PART_TYPE,
} from "./ConfiguratorData";

export class ConfiguratorViewerController extends Entity {
  private _variant?: ConfiguratorVariant;
  private _showMeter: boolean = false;
  private _modelConfigurator?: SugarModelConfigurator;
  private _newModelConfigurator?: SugarModelConfigurator;
  private _nextCameraPreset: string | null = null;
  private _configuratorId = "default";

  set configuratorId(value: string) {
    this._configuratorId = value;
  }

  private get _configData(): CONFIGURATOR_MODEL_TYPE {
    return getConfiguratorData(this._configuratorId);
  }

  private _nextCameraPresetHandler = (e: Event) => {
    const detail = (e as CustomEvent).detail ?? null;
    this._nextCameraPreset = detail.preset ?? null;
    this._showMeter = detail.showMeter ?? false;
  };

  private _modelDimensions!: SugarModelDimensions; // = new SugarModelDimensions();

  set variant(value: ConfiguratorVariant) {
    this.changeVariant(value);
    this._variant = value;
  }

  set showMeter(value: boolean) {
    if (value) {
      const sizes = getVariantAttr(this._variant!, "size")
        ?.split("x")
        .map((x) => parseFloat(x));

      this._modelDimensions.show(
        this._modelConfigurator ? [this._modelConfigurator] : [],
        false,
        sizes ? new Vector3(sizes[0]/100, 0, sizes[1]/100) : undefined,
      );
      this.fitCamera();
    } else {
      this._modelDimensions.hide();
    }

    this._showMeter = value;
  }

  getBaseConfig(value: ConfiguratorVariant) {
    const mainConfig: Map<string, MODEL_PART_TYPE> = new Map();
    const legTypeVal = getVariantAttr(value, "legType");
    const legType = this._configData.configurator.legs?.filter(
      (leg) => leg.name === legTypeVal || leg.name === legTypeVal + " Ayak",
    )[0];
    const backType = this._configData.configurator.backs?.filter(
      (back) => back.name === getVariantAttr(value, "backCushion"),
    )[0];

    if (legType) {
      mainConfig.set(
        legType.type === "Silindir Ahşap"
          ? "Silindir Ahşap Ayak"
          : legType.type,
        legType,
      );
    }

    if (backType) {
      mainConfig.set(backType.type, backType);
    }
    return mainConfig;
  }

  getSideConfig(value: ConfiguratorVariant) {
    const sideConfig: Map<string, MODEL_PART_TYPE> = new Map();
    const sideType = this._configData.configurator.sides?.filter(
      (side) => side.name === getVariantAttr(value, "armType"),
    )[0];
    if (sideType) {
      sideConfig.set(sideType.type, sideType);
    }
    return sideConfig;
  }

  onAdd(engine: Engine): void {
    this._modelDimensions = new SugarModelDimensions();
    engine.addEventListener(
      "next-camera-preset",
      this._nextCameraPresetHandler,
    );
  }

  onRemove(engine: Engine): void {
    engine.removeEventListener(
      "next-camera-preset",
      this._nextCameraPresetHandler,
    );
  }

  findMaterial(
    code: string,
    materialPartGroups: {
      title: string;
      code: string;
      materialGroups: {
        name: string;
        order: number;
        materials: any[];
      }[];
    }[],
    value: string[],
  ) {
    const materialPartGroup = materialPartGroups.filter(
      (mpg) => mpg.code === code,
    )[0];
    if (!materialPartGroup) {
      logger.warn("materialPartGroup yok (code)", { code });
      return { code, material: undefined };
    }
    const material = materialPartGroup.materialGroups
      .reduce((prev: any[], current) => {
        return [...prev, ...current.materials];
      }, [])
      .find((material) =>
        value.find((v) => material.name && material.name?.trim() === v),
      );
    return { code: code, material: material };
  }

  /**
   * Configurator zip / modül JSON'da `fabricMaterialPartGroupCode` ve `legMaterialPartGroupCode`
   * tanımlı değilse `materialPartGroups[].title` üzerinden tahmin; yine yetmezse eski varsayılan 1/2.
   */
  private _resolveMaterialPartGroupCodes(): {
    fabricCode: string;
    legCode: string;
  } {
    const cfg = this._configData.configurator;
    const explicitFabric = cfg.fabricMaterialGroupCode?.trim();
    const explicitLeg = cfg.legMaterialGroupCode?.trim();

    const fabricCode = explicitFabric || "1";
    const legCode = explicitLeg || "2";

    return { fabricCode, legCode };
  }

  getMaterialConfig(
    value: ConfiguratorVariant,
  ): Map<string, { code: string; material: any }> {
    const materialConfig: Map<
      string,
      {
        code: string;
        material: any;
      }
    > = new Map();
    const mpg = this._configData.materialPartGroups;
    const { fabricCode, legCode } = this._resolveMaterialPartGroupCodes();

    const legMaterial = this.findMaterial(legCode, mpg, [
      getVariantAttr(value, "legColor") ?? "",
    ]);
    materialConfig.set(legMaterial.code, legMaterial);

    const fabricType = getVariantAttr(value, "fabricType") ?? "";
    const fabricColor = getVariantAttr(value, "fabricColor") ?? "";
    const fabricMaterial = this.findMaterial(fabricCode, mpg, [
      fabricColor,
      fabricType + "-" + fabricColor,
      fabricType + " " + fabricColor,
    ]);

    materialConfig.set(fabricMaterial.code, fabricMaterial);

    return materialConfig;
  }

  async changeVariant(value: ConfiguratorVariant) {
    const bodyMap = this._configData.configurator.body;
    if (!bodyMap || typeof bodyMap !== "object") {
      logger.warn("configurator.body tanımlı değil", { value });
      return;
    }

    const sizeStr = getVariantAttr(value, "size");
    const bodyStr = getVariantAttr(value, "body");

    const modelIdsToLoad =
      (sizeStr && bodyMap[sizeStr]?.length ? bodyMap[sizeStr] : undefined) ??
      (bodyStr && bodyMap[bodyStr]?.length ? bodyMap[bodyStr] : undefined);

    if (!modelIdsToLoad?.length) {
      logger.warn("modelIdsToLoad yok", { sizeStr, bodyStr, value });
      return;
    }

    const mainConfig = this.getBaseConfig(value);
    const sideConfig = this.getSideConfig(value);
    const materialConfig = this.getMaterialConfig(value);

    this._newModelConfigurator = new SugarModelConfigurator(
      mainConfig,
      sideConfig,
      materialConfig,
      this._configData,
    );

    this._newModelConfigurator.addEventListener(
      "configured" as any,
      this.checkFinish.bind(this),
    );

    const modelsToLoad = modelIdsToLoad
      .map((id) => this._configData.models.filter((mm) => mm.id == id)[0])
      .filter((m) => m)
      .map((m) =>
        new SugarModelGroup(m, this._configData).load(
          { data: m, partConfig: mainConfig },
          { forceSync: true },
        ),
      );

    await Promise.all(modelsToLoad).then(async (loadeds) => {
      let i = 1;
      let placeX = this._newModelConfigurator?.position.x ?? 0;
      for (const loaded of loadeds) {
        placeX +=1;
        loaded?.position.set(placeX , 0, 0);
        loaded?.updateWorldMatrix(true, true);
        console.log("this._newModelConfigurator?.addModelWithoutComplete(loaded!, );", loaded?.userData)
        this._newModelConfigurator?.addModelWithoutComplete(loaded!, 2);
        await this._newModelConfigurator?.completeSync();
      }
      this._newModelConfigurator?.position.set(0, 0, 0);
    });
  }

  private _in = false;
  private checkFinish() {
    logger.info("checkFinish");
    if (this._modelConfigurator == this._newModelConfigurator && !this._in)
      return;

    this._in = true;
    const info = (
      Engine.activeEngine?.renderer.instance as
        | {
            info?: {
              memory: { geometries: number; textures: number };
              programs?: unknown[];
            };
          }
        | undefined
    )?.info;
    if (info) {
      logger.info(
        "[variant-probe] pre-swap",
        "geom=" + info.memory.geometries,
        "tex=" + info.memory.textures,
        "prog=" + (info.programs?.length ?? 0),
      );
    }
    this._modelConfigurator?.removeFromParent();
    this._modelConfigurator?.dispose();
    this._modelConfigurator = this._newModelConfigurator;

    const box = new Box3();
    box.setFromObject(this._newModelConfigurator!);
    const center = new Vector3();
    box.getCenter(center);
    this._newModelConfigurator!.position.set(-center.x, 0, -center.z);

    Engine.activeEngine.activeScene?.add(this._newModelConfigurator!);
    Engine.activeEngine.canvas.dispatchEvent(
      new CustomEvent("configurator-updated", { bubbles: true }),
    );
    setTimeout(() => {
      if (this._showMeter) {
        const sizes = getVariantAttr(this._variant!, "size")
          ?.split("x")
          .map((x) => parseFloat(x));

        this._modelDimensions.hide();
        this._modelDimensions.show(
          [this._newModelConfigurator!],
          false,
          sizes ? new Vector3(sizes[0]/100, 0, sizes[1]/100) : undefined,
        );
      }
      this.fitCamera();
    }, 10);

    if (info) {
      logger.info(
        "[variant-probe] post-swap",
        "geom=" + info.memory.geometries,
        "tex=" + info.memory.textures,
        "prog=" + (info.programs?.length ?? 0),
      );
    }
    this._in = false;
  }

  fitCamera() {
    const preset = this._nextCameraPreset ?? undefined;
    this._nextCameraPreset = null;
    Engine.activeEngine?.dispatchCustomEvent("fit-camera", {
      detail: { object: this._modelConfigurator!, preset },
    });
  }

  /**
   * Aktif kameraya / orbit'e dokunmadan, verilen preset için geçici bir
   * PerspectiveCamera oluşturur, sahneyi engine.renderSnapshot ile bir kere
   * render eder ve oluşan blob'u döner.
   */
  async renderSnapshot(
    preset: CameraPresetId,
    options: { width: number; height: number; type?: string; quality?: number },
  ): Promise<Blob | null> {
    const engine = Engine.activeEngine;
    if (!engine) return null;
    if (!this._modelConfigurator) return null;

    const box = new Box3().setFromObject(this._modelConfigurator, true);
    if (box.isEmpty()) return null;

    const activeCam = engine.activeCamera?.instance as
      | (PerspectiveCamera & { fov?: number })
      | undefined;
    const fovDeg = activeCam?.fov ?? 25;
    const aspect = options.width / options.height;

    const { position, target } = getCameraPresetFromBox(
      box,
      preset,
      fovDeg,
      aspect,
    );

    const tempCam = new PerspectiveCamera(
      fovDeg,
      aspect,
      CAMERA_NEAR,
      CAMERA_FAR,
    );
    tempCam.position.copy(position);
    tempCam.lookAt(target);
    tempCam.updateMatrixWorld(true);
    tempCam.updateProjectionMatrix();

    return engine.renderSnapshot(tempCam, options);
  }
}
